/**
 * Los tipos que el evaluador necesita del host, y nada más.
 *
 * Están acá y no importados del motor documental para que el paquete sea PURO: la API no tiene
 * `@/document-engine`, y un evaluador compartido que dependa del árbol de la web no se puede
 * compartir.
 */

/**
 * EL VOCABULARIO DE TIPOS, y es uno solo.
 *
 * Decide si `2026-01-31` se parsea como fecha o se compara como texto, y es lo único que mira
 * `operatorsForType` para ofrecer un operador y no otro. Vive acá —y no en `@idc-forms/schemas`—
 * porque el único consumidor que se COMPORTA distinto según el tipo está en este paquete
 * (ADR 0036 §1). La hoja no depende del tronco.
 *
 * Const object + union type, nunca `enum` de TypeScript (regla 1 del monorepo).
 *
 * ⚠️ `ENUM` está en desuso desde el ADR 0036 §3: el dominio cerrado se declara con `options` y
 * `enum` ya es una DERIVACIÓN de que las haya. Sigue acá porque el catálogo de documentos lo
 * escribe hoy en 14 lugares; lo jubila la ola 3, no antes.
 */
export const VARIABLE_TYPE = {
  STRING: 'string',
  NUMBER: 'number',
  DATE: 'date',
  BOOLEAN: 'boolean',
  ENUM: 'enum',
  EMAIL: 'email',
  FILE: 'file',
  /**
   * ⭐ Un DESTINO, no un texto que casualmente empieza con `https`.
   *
   * Nació con el botón «Ver expediente» del correo de Créditos: cuando el marco dejó de inyectarlo,
   * su enlace pasó a ser una variable que alguien pone en el `href` de un botón. Y ahí el tipo
   * decide algo real —`emailDocHref` resuelve el token y después exige `https:`—, así que llamarla
   * `string` habría sido esconder que ese valor tiene una forma que se hace cumplir.
   */
  URL: 'url'
} as const

/** Tipo declarado de un token. Decide si `2026-01-31` se parsea como fecha o se compara como texto. */
export type VariableType = (typeof VARIABLE_TYPE)[keyof typeof VARIABLE_TYPE]

/**
 * El valor de un campo de un item. Normalmente texto ya formateado; un ARRAY es el soporte de
 * colecciones ANIDADAS (1 nivel), que el motor de loops expone bajo `alias.<campo>`.
 */
export type CollectionValue = string | Array<Record<string, string>>

export type CollectionRecord = Record<string, CollectionValue>

/** Colecciones por nombre (`clientes`, `cuotas`). Sólo las usa `unir(...)`. */
export type Collections = Record<string, CollectionRecord[]>

/** Cómo se tipa un token. `undefined` = sin declarar → se trata como texto. */
export type TypeOf = (token: string) => VariableType | undefined
